import { useEffect, useRef, type ReactNode } from 'react';

// Minimal accessible modal built on the native <dialog> element.
// HTMLDialogElement.showModal() handles focus trapping, ESC-to-close,
// and the backdrop layer for us. We add: scrolled body lock, explicit
// "click outside to close" (showModal's default backdrop click does
// nothing), and a header with a close button.
//
// Intentionally minimal — no animations, no portals, no router-state
// coupling. Callers control open via a parent boolean; the dialog
// element is mounted/unmounted with that boolean rather than always
// present, so re-opens always start at a clean state.

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  // Optional id for the dialog's heading element so callers can
  // wire aria-describedby from nested form fields, etc.
  titleId?: string;
  children: ReactNode;
  // Optional footer node — typically Cancel / Save buttons. Renders
  // inside the dialog so it stays at the bottom on long forms.
  footer?: ReactNode;
  // Defaults to a sensible mid-width; callers can override for
  // wider forms (edit driver: many fields → wider).
  widthClass?: string;
};

export function Modal({
  open,
  onClose,
  title,
  titleId,
  children,
  footer,
  widthClass = 'max-w-lg',
}: Props): JSX.Element | null {
  const ref = useRef<HTMLDialogElement | null>(null);

  // Drive native open/close imperatively. .showModal() applies the
  // ::backdrop pseudo-element + focus trap. Closing via .close()
  // fires the dialog's "close" event, which we re-route to onClose.
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      try {
        dialog.showModal();
      } catch {
        // showModal throws if the dialog is already open or detached;
        // we mounted/unmounted via `open`, so this should not happen,
        // but the try/catch keeps a bad refresh from crashing the
        // page during a fast remount.
      }
    }
    if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  // Clicking the ::backdrop generates a click on the dialog element
  // itself (target === dialog). Clicking inside content bubbles up
  // from a descendant. Distinguish the two to support backdrop-close.
  const onDialogClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!open) return null;

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onClose={onClose}
      onClick={onDialogClick}
      className={`m-auto rounded-lg border border-border bg-bg p-0 backdrop:bg-ink/40 ${widthClass} w-[calc(100%-2rem)]`}
    >
      {/* Inner wrapper absorbs clicks so backdrop-vs-content
          distinction in onDialogClick works. */}
      <div className="flex flex-col" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 id={titleId} className="text-base font-medium text-ink">
            {title}
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="rounded-md p-1 text-ink-2 hover:bg-bg-soft hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            ✕
          </button>
        </header>
        <div className="px-4 py-4">{children}</div>
        {footer ? (
          <footer className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
            {footer}
          </footer>
        ) : null}
      </div>
    </dialog>
  );
}
